import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import pdfjsWorker from "react-pdf/node_modules/pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;


const MAX_SCALE = 3;
const MIN_SCALE = 0.25;

const PDFEditorCanvas = ({ pdfUrl, floorPlanData, selectedLot, onLotSelect }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const [scale, setScale] = useState(1.0);
  const [fitScale, setFitScale] = useState(1.0);

  const [error, setError] = useState(null);

  // ✅ si PDF.js falla, mostramos iframe nativo del navegador
  const [useNativeViewer, setUseNativeViewer] = useState(false);

  const containerRef = useRef(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setError(null);
    setUseNativeViewer(false);
    setPageNumber(1);
  };

  const onDocumentLoadError = (err) => {
    console.error("PDF load error:", err);

    // ✅ Activamos fallback nativo
    setUseNativeViewer(true);

    setError(
      "El visor PDF avanzado no pudo decodificar este documento (JPX/JPEG2000). Se usará el visor nativo."
    );
  };

  const handlePageLoadSuccess = (page) => {
    try {
      const containerWidth = containerRef?.current?.clientWidth || 0;
      if (!containerWidth) return;

      const viewport = page?.getViewport({ scale: 1 });
      const pageWidth = viewport?.width;

      const padding = 24;
      const availableWidth = Math.max(containerWidth - padding, 100);

      const nextFit = availableWidth / pageWidth;
      const clamped = Math.min(Math.max(nextFit, MIN_SCALE), MAX_SCALE);

      setFitScale(clamped);
      setScale(clamped);
      setError(null);
    } catch (e) {
      console.error("Fit scale error:", e);
    }
  };

  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      setScale((prev) => {
        if (!fitScale || fitScale <= 0) return prev;
        return fitScale;
      });
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [fitScale]);

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.2, MAX_SCALE));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.2, MIN_SCALE));
  const handleResetZoom = () => setScale(fitScale);

  const getLotColor = (lot) => {
    if (lot?.status === "available") return "rgba(34, 197, 94, 0.3)";
    if (lot?.status === "reserved") return "rgba(251, 191, 36, 0.3)";
    if (lot?.status === "sold") return "rgba(239, 68, 68, 0.3)";
    return "rgba(59, 130, 246, 0.3)";
  };

  const getLotBorderColor = (lot) => {
    if (selectedLot && selectedLot?.number === lot?.number) return "rgba(139, 92, 246, 1)";
    if (lot?.status === "available") return "rgba(34, 197, 94, 1)";
    if (lot?.status === "reserved") return "rgba(251, 191, 36, 1)";
    if (lot?.status === "sold") return "rgba(239, 68, 68, 1)";
    return "rgba(59, 130, 246, 1)";
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-primary" />
          Vista del Plano
        </h3>

        {!useNativeViewer && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleZoomOut} disabled={scale <= MIN_SCALE}>
              <Icon name="ZoomOut" size={18} />
            </Button>

            <span className="text-sm text-muted-foreground min-w-[60px] text-center">
              {Math.round(scale * 100)}%
            </span>

            <Button variant="outline" size="sm" onClick={handleZoomIn} disabled={scale >= MAX_SCALE}>
              <Icon name="ZoomIn" size={18} />
            </Button>

            <Button variant="outline" size="sm" onClick={handleResetZoom} title="Ajustar al contenedor">
              <Icon name="Maximize2" size={18} />
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-error/10 border border-error rounded-lg flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
          <p className="text-error text-sm">{error}</p>
        </div>
      )}

      <div
        ref={containerRef}
        className="relative bg-muted rounded-lg overflow-auto w-full"
        style={{ maxHeight: "70vh" }}
      >
        {pdfUrl ? (
          useNativeViewer ? (
            // ✅ Fallback: visor nativo (siempre funciona)
            <iframe
              title="PDF Viewer"
              src={pdfUrl}
              style={{ width: "100%", height: "70vh", border: "0" }}
            />
          ) : (
            <div className="relative inline-block">
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex items-center justify-center p-12">
                    <Icon name="Loader2" size={48} className="animate-spin text-primary" />
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  onLoadSuccess={handlePageLoadSuccess}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>

              {/* Overlay solo si NO usamos viewer nativo */}
              {floorPlanData?.lots?.length > 0 && (
                <svg className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{ zIndex: 10 }}>
                  {floorPlanData?.lots?.map((lot, index) => {
                    if (!lot?.coordinates) return null;

                    return (
                      <g key={`lot-${lot?.number}-${index}`}>
                        <rect
                          x={lot?.coordinates?.x * scale}
                          y={lot?.coordinates?.y * scale}
                          width={lot?.coordinates?.width * scale}
                          height={lot?.coordinates?.height * scale}
                          fill={getLotColor(lot)}
                          stroke={getLotBorderColor(lot)}
                          strokeWidth={selectedLot?.number === lot?.number ? 3 : 2}
                          className="pointer-events-auto cursor-pointer transition-all hover:opacity-80"
                          onClick={() => onLotSelect(lot)}
                        />
                        <text
                          x={(lot?.coordinates?.x + lot?.coordinates?.width / 2) * scale}
                          y={(lot?.coordinates?.y + lot?.coordinates?.height / 2) * scale}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="pointer-events-none"
                          style={{ fontSize: `${12 * scale}px` }}
                        >
                          {lot?.number}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              )}
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <Icon name="FileQuestion" size={64} className="text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-2">No hay PDF cargado</p>
            <p className="text-sm text-muted-foreground">Carga un PDF del plano para comenzar a editar</p>
          </div>
        )}
      </div>

      {numPages && !useNativeViewer && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
            disabled={pageNumber <= 1}
          >
            <Icon name="ChevronLeft" size={18} className="mr-1" />
            Anterior
          </Button>

          <span className="text-sm text-muted-foreground">
            Página {pageNumber} de {numPages}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageNumber((prev) => Math.min(prev + 1, numPages))}
            disabled={pageNumber >= numPages}
          >
            Siguiente
            <Icon name="ChevronRight" size={18} className="ml-1" />
          </Button>
        </div>
      )}

      {floorPlanData?.lots && (
        <div className="mt-4 p-3 bg-success/10 border border-success rounded-lg">
          <p className="text-xs text-success flex items-center gap-2">
            <Icon name="CheckCircle" size={16} />
            {floorPlanData?.lots?.length} lotes detectados • Confianza:{" "}
            {Math.round((floorPlanData?.confidence || 0) * 100)}%
          </p>
        </div>
      )}
    </div>
  );
};

export default PDFEditorCanvas;
